import { Tooltip } from '@/components/tooltip.js';
import type { DiagnosticDecodeGapSummary } from '@airreading/common/data';
import { Info } from 'lucide-react';

function formatRate(rate: number | null): string {
  return rate === null ? 'N/A' : `${Number(rate.toFixed(2))}%`;
}

function formatGap(gapPercentagePoints: number | null): string {
  return gapPercentagePoints === null ? 'N/A' : `${Number(gapPercentagePoints.toFixed(2))} percentage points`;
}

export function DiagnosticDecodeGapSummary({
  gap,
  testId,
  infoButtonTestId,
}: {
  gap: DiagnosticDecodeGapSummary;
  testId?: string;
  infoButtonTestId?: string;
}) {
  const pseudowordRate = formatRate(gap.pseudowordMissRate);
  const regularRate = formatRate(gap.regularWordMissRate);
  const gapLabel = formatGap(gap.gapPercentagePoints);

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid={testId}>
      <span>
        Missed words: {gap.pseudowordsMissed} pseudowords • {gap.regularWordsMissed} regular words
      </span>
      <Tooltip
        asChild
        trigger={
          <button
            type="button"
            className="inline-flex rounded-full text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            data-testid={infoButtonTestId}
          >
            <Info aria-hidden className="h-4 w-4" />
            <span className="sr-only">Missed word rates</span>
          </button>
        }
      >
        <div className="space-y-1 text-sm">
          <p>
            Pseudowords: {gap.pseudowordsMissed} of {gap.pseudowordsSeen} missed ({pseudowordRate})
          </p>
          <p>
            Regular words: {gap.regularWordsMissed} of {gap.regularWordsSeen} missed ({regularRate})
          </p>
          <p>Gap: {gapLabel}</p>
        </div>
      </Tooltip>
      {gap.possibleDecodingGap && <span>Possible decoding gap</span>}
    </div>
  );
}
